/**
 * HelpPage (/help)
 * Single entry point for the Help module. The "Help Center" tab holds the
 * reader-facing list (categories as cards, articles within each, filterable by
 * title); admins additionally get "Articles" and "Categories" management tabs
 * here, so the sidebar needs only one Help link.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Search, BookOpen, FileText, ChevronRight, FileQuestion } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import Tabs, { TabItem } from '@components/common/Tabs';
import ErrorBoundary from '@components/common/ErrorBoundary';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import { usePermissions } from '@contexts/AuthContext';
import { useHelpPageData } from '@hooks/useHelpPageData';
import { HelpArticlesTab, HelpCategoriesTab } from '@components/features/help';
import styles from './HelpPage.module.css';

const HELP_CENTER_TAB = 'help-center';
const ARTICLES_TAB = 'articles';
const CATEGORIES_TAB = 'categories';

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();
  const { groups, isLoading, error } = useHelpPageData();
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();

  // Categories whose own name matches are kept whole; otherwise the category
  // survives only for the articles that match, so a search never shows a
  // category with articles unrelated to the query.
  const filteredGroups = useMemo(() => {
    if (!query) return groups;

    return groups
      .map((group) => {
        if (group.category.name.toLowerCase().includes(query)) return group;
        return {
          ...group,
          articles: group.articles.filter((article) => article.title.toLowerCase().includes(query))
        };
      })
      .filter((group) => group.articles.length > 0);
  }, [groups, query]);

  const matchCount = useMemo(
    () => filteredGroups.reduce((total, group) => total + group.articles.length, 0),
    [filteredGroups]
  );

  if (isLoading) {
    return <LoadingState message="Loading help articles..." spinnerSize="large" />;
  }

  if (error) {
    return <ErrorState title="Error" message="Error loading help articles. Please try again." />;
  }

  if (groups.length === 0) {
    return (
      <div className={styles.emptyState} data-test-id="help-empty-state">
        <FileQuestion size={40} className={styles.emptyStateIcon} />
        <div className={styles.emptyStateTitle}>No help articles available yet</div>
        <div className={styles.emptyStateText}>
          Articles published by an administrator will appear here.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.searchRow}>
        <div className={styles.searchField}>
          <Search size={16} className={styles.searchIcon} aria-hidden="true" />
          <input
            type="search"
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search help articles..."
            aria-label="Search help articles"
            data-test-id="help-search-input"
          />
        </div>
        {query && (
          <span className={styles.resultCount} role="status" data-test-id="help-search-count">
            {matchCount} {matchCount === 1 ? 'article' : 'articles'} found
          </span>
        )}
      </div>

      {filteredGroups.length === 0 ? (
        <div className={styles.emptyState} data-test-id="help-no-results">
          <Search size={40} className={styles.emptyStateIcon} />
          <div className={styles.emptyStateTitle}>No matching articles</div>
          <div className={styles.emptyStateText}>
            Nothing matched &ldquo;{search.trim()}&rdquo;. Try a different search term.
          </div>
        </div>
      ) : (
        <div className={styles.categoryGrid}>
          {filteredGroups.map((group) => (
            <section
              key={group.category.id}
              className={styles.categorySection}
              data-test-id={`help-category-${group.category.id}`}
            >
              <div className={styles.categoryHeader}>
                <span className={styles.categoryIcon} aria-hidden="true">
                  <BookOpen size={16} />
                </span>
                <h2 className={styles.categoryTitle}>{group.category.name}</h2>
                <span className={styles.categoryCount}>{group.articles.length}</span>
              </div>

              {group.articles.length === 0 ? (
                <p className={styles.emptyCategory}>No articles in this category yet.</p>
              ) : (
                <ul className={styles.articleList}>
                  {group.articles.map((article) => (
                    <li key={article.id}>
                      <button
                        type="button"
                        className={styles.articleLink}
                        onClick={() => navigate(`/help/${article.id}`)}
                        data-test-id={`help-article-link-${article.id}`}
                      >
                        <FileText size={16} className={styles.articleLinkIcon} aria-hidden="true" />
                        <span className={styles.articleLinkText}>{article.title}</span>
                        <ChevronRight size={16} className={styles.articleLinkChevron} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}
    </>
  );
};

const HelpPage: React.FC = () => {
  const { isAdminStrict } = usePermissions();
  const [activeTab, setActiveTab] = useState(HELP_CENTER_TAB);

  const tabs: TabItem[] = useMemo(() => {
    const items: TabItem[] = [{ key: HELP_CENTER_TAB, label: 'Help Center' }];
    if (isAdminStrict) {
      items.push({ key: ARTICLES_TAB, label: 'Manage Articles' });
      items.push({ key: CATEGORIES_TAB, label: 'Manage Categories' });
    }
    return items;
  }, [isAdminStrict]);

  // Guards against a non-admin landing on a management tab, e.g. if the user's
  // permissions change while the page is open.
  const currentTab = tabs.some((tab) => tab.key === activeTab) ? activeTab : HELP_CENTER_TAB;

  const renderTabContent = () => {
    switch (currentTab) {
      case ARTICLES_TAB:
        return (
          <ErrorBoundary level="page">
            <HelpArticlesTab />
          </ErrorBoundary>
        );
      case CATEGORIES_TAB:
        return (
          <ErrorBoundary level="page">
            <HelpCategoriesTab />
          </ErrorBoundary>
        );
      case HELP_CENTER_TAB:
      default:
        return <HelpCenter />;
    }
  };

  return (
    <PageLayout title="Help">
      <div className={styles.helpPage} data-test-id="help-page">
        <header className={styles.pageHeader}>
          <span className={styles.headerIcon} aria-hidden="true">
            <HelpCircle size={24} />
          </span>
          <div className={styles.headerText}>
            <h1>Help Center</h1>
            <p className={styles.headerSubtitle}>
              Browse guides and reference articles for working with the platform.
            </p>
          </div>
        </header>

        {tabs.length > 1 && (
          <Tabs items={tabs} activeTab={currentTab} onTabChange={setActiveTab} />
        )}

        {renderTabContent()}
      </div>
    </PageLayout>
  );
};

export default HelpPage;
